'use client'

import * as AvatarWrapper from '@radix-ui/react-avatar'
import { cva } from 'class-variance-authority'
import type { MouseEventHandler } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  AttendIcon,
  CameraIcon,
  CancelAttendIcon,
  DefaultAvatarImg,
  DefaultLogoImg,
  LogoATS,
  VerifyIcon
} from '~/core/ui/FillIcons'
import { cn } from '~/core/ui/utils'

export const defaultColorBgAvatar = '#E4EEFD'

const randomColor = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

const listAvatarInitials: { [id: string]: string } = {
  '#F8EDED':
    'bg-ava-bg-50 text-ava-text-50 dark:text-ava-bg-50 dark:bg-ava-text-50',
  '#F8EBE2':
    'bg-ava-bg-100 text-ava-text-100 dark:text-ava-bg-100 dark:bg-ava-text-100',
  '#F8F3E2':
    'bg-ava-bg-200 text-ava-text-200 dark:text-ava-bg-200 dark:bg-ava-text-200',
  '#EAF0E7':
    'bg-ava-bg-300 text-ava-text-300 dark:text-ava-bg-300 dark:bg-ava-text-300',
  '#E1EFE9':
    'bg-ava-bg-400 text-ava-text-400 dark:text-ava-bg-400 dark:bg-ava-text-400',
  '#E3EFF0':
    'bg-ava-bg-500 text-ava-text-500 dark:text-ava-bg-500 dark:bg-ava-text-500',
  '#E4EEFD':
    'bg-ava-bg-600 text-ava-text-600 dark:text-ava-bg-600 dark:bg-ava-text-600',
  '#DFE3FF':
    'bg-ava-bg-700 text-ava-text-700 dark:text-ava-bg-700 dark:bg-ava-text-700',
  '#E7E4F8':
    'bg-ava-bg-800 text-ava-text-800 dark:text-ava-bg-800 dark:bg-ava-text-800',
  '#F1E8FA':
    'bg-ava-bg-900 text-ava-text-900 dark:text-ava-bg-900 dark:bg-ava-text-900',
  '#FFFFFF': 'bg-white text-gray-900 border border-[1.5px] border-gray-300'
}

const avatarRootVariants = cva(
  `relative flex hover:after:absolute hover:after:h-full hover:after:w-full hover:after:bg-black hover:after:opacity-25 hover:after:content-['']`,
  {
    variants: {
      size: {
        '2xs': 'h-4 w-4',
        xs: 'h-5 w-5',
        sm: 'h-6 w-6',
        md: 'h-8 w-8',
        lg: 'h-10 w-10',
        xl: 'h-12 w-12',
        '2xl': 'h-14 w-14',
        '3xl': 'h-16 w-16'
      },
      shape: {
        circular: 'rounded-full hover:after:rounded-full',
        rounded: 'rounded-xs hover:after:rounded-xs'
      },
      group: {
        default: '',
        group: 'ring-2 ring-white dark:ring-gray-900'
      }
    },
    defaultVariants: {
      size: '3xl',
      shape: 'circular',
      group: 'default'
    }
  }
)

const avatarVariants = cva(
  `focus:ring-primary-300 dark:focus:ring-primary-700 inline-block h-full w-full object-cover focus:ring-1 focus:outline-hidden focus-visible:ring-1`,
  {
    variants: {
      group: {
        default: '',
        group: 'ring-2 ring-white dark:ring-gray-900'
      },
      shape: {
        circular: 'rounded-full',
        rounded: 'rounded-xs'
      }
    },
    defaultVariants: {
      group: 'default',
      shape: 'circular'
    }
  }
)

const avatarStatusVariants = cva(
  'absolute right-0 bottom-0 block translate-x-0 -translate-y-0 transform ring-white dark:ring-gray-900',
  {
    variants: {
      size: {
        '2xs': 'h-1 w-1 ring-1',
        xs: 'h-1.5 w-1.5 ring-2',
        sm: 'h-2 w-2 ring-2',
        md: 'h-2.5 w-2.5 ring-2',
        lg: 'h-3 w-3 ring-2',
        xl: 'h-3.5 w-3.5 ring-2',
        '2xl': 'h-4 w-4 ring-2',
        '3xl': 'h-[18px] w-[18px] ring-2'
      },
      shape: {
        circular: 'rounded-full',
        rounded: 'rounded-xs'
      },
      status: {
        online: 'bg-green-500',
        offline: 'bg-gray-300'
      }
    },
    defaultVariants: {
      size: '3xl',
      shape: 'circular',
      status: 'online'
    }
  }
)

const avatarVerifyVariants = cva(
  'absolute right-0 bottom-0 block translate-x-0 -translate-y-0 transform',
  {
    variants: {
      size: {
        '2xs': 'h-1 w-1',
        xs: 'h-1.5 w-1.5',
        sm: 'h-2 w-2',
        md: 'h-2.5 w-2.5',
        lg: 'h-3 w-3',
        xl: 'h-3.5 w-3.5',
        '2xl': 'h-4 w-4',
        '3xl': 'h-[18px] w-[18px]'
      },
      shape: {
        circular: 'rounded-full',
        rounded: 'rounded-xs'
      }
    },
    defaultVariants: {
      size: '3xl',
      shape: 'circular'
    }
  }
)

const avatarOverlayVariants = cva('absolute top-0 left-0', {
  variants: {
    size: {
      '2xs': 'h-4 w-4',
      xs: 'h-5 w-5',
      sm: 'h-6 w-6',
      md: 'h-8 w-8',
      lg: 'h-10 w-10',
      xl: 'h-12 w-12',
      '2xl': 'h-14 w-14',
      '3xl': 'h-16 w-16'
    },
    disabled: {
      default: '',
      disabled: 'bg-white opacity-30 hover:bg-gray-900'
    },
    alt: {
      default: '',
      alt: 'dark:hover:bg-gray-900'
    },
    shape: {
      circular: 'rounded-full',
      rounded: 'rounded-xs'
    }
  },
  defaultVariants: {
    size: '3xl',
    disabled: 'default',
    alt: 'default',
    shape: 'circular'
  }
})

const avatarUploadOverlayVariants = cva(
  'absolute top-0 left-0 flex cursor-pointer items-end justify-center opacity-0 hover:bg-black/25 hover:opacity-100',
  {
    variants: {
      size: {
        '2xs': 'h-4 w-4',
        xs: 'h-5 w-5',
        sm: 'h-6 w-6',
        md: 'h-8 w-8',
        lg: 'h-10 w-10',
        xl: 'h-12 w-12',
        '2xl': 'h-14 w-14',
        '3xl': 'h-16 w-16'
      },
      disabled: {
        default: '',
        disabled: 'bg-black opacity-30 hover:bg-gray-900'
      },
      shape: {
        circular: 'rounded-full',
        rounded: 'rounded-xs'
      }
    },
    defaultVariants: {
      size: '3xl',
      disabled: 'default',
      shape: 'circular'
    }
  }
)

const avatarUploadOverlayIconVariants = cva(
  'flex items-center justify-center',
  {
    variants: {
      size: {
        '2xs': 'mb-0.5 h-[5px] w-1.5',
        xs: 'mb-0.5 h-[5px] w-1.5',
        sm: 'mb-[3px] h-1.5 w-2',
        md: 'mb-1 h-1.5 w-2',
        lg: 'mb-1 h-2 w-2.5',
        xl: 'mb-[5px] h-2 w-2.5',
        '2xl': 'mb-1.5 h-2 w-2.5',
        '3xl': 'mb-[7px] h-2 w-2.5'
      }
    },
    defaultVariants: {
      size: '3xl'
    }
  }
)

const avatarFallbackRootVariants = cva(
  'flex h-full w-full items-center justify-center',
  {
    variants: {
      group: {
        default: '',
        group: 'ring-2 ring-white dark:ring-gray-900'
      },
      shape: {
        circular: 'rounded-full',
        rounded: 'rounded-xs'
      }
    },
    defaultVariants: {
      group: 'default',
      shape: 'circular'
    }
  }
)

const avatarPlaceholderVariants = cva(
  'inline-block h-full w-full overflow-hidden bg-gray-200 text-white dark:bg-gray-600 dark:text-gray-400',
  {
    variants: {
      shape: {
        circular: 'rounded-full',
        rounded: 'rounded-xs'
      }
    },
    defaultVariants: {
      shape: 'circular'
    }
  }
)

const avatarFallbackFocusVariants = cva(
  'focus:ring-primary-300 dark:focus:ring-primary-700 flex h-full w-full items-center justify-center focus:ring-1 focus:outline-hidden focus-visible:ring-1',
  {
    variants: {
      shape: {
        circular: 'rounded-full',
        rounded: 'rounded-xs'
      }
    },
    defaultVariants: {
      shape: 'circular'
    }
  }
)

const avatarFallbackVariants = cva(
  'font-medium text-gray-600 dark:text-gray-300',
  {
    variants: {
      size: {
        '2xs': 'text-6xs',
        xs: 'text-6xs',
        sm: 'text-5xs',
        md: 'text-2xs',
        lg: 'text-sm',
        xl: 'text-base',
        '2xl': 'text-lg',
        '3xl': 'text-xl'
      }
    },
    defaultVariants: {
      size: '3xl'
    }
  }
)

const avatarCountRootVariants = cva(
  'relative flex items-center justify-center',
  {
    variants: {
      size: {
        '2xs': 'h-4 w-4',
        xs: 'h-5 w-5',
        sm: 'h-6 w-6',
        md: 'h-8 w-8',
        lg: 'h-10 w-10',
        xl: 'h-12 w-12',
        '2xl': 'h-14 w-14',
        '3xl': 'h-16 w-16'
      }
    },
    defaultVariants: {
      size: '3xl'
    }
  }
)

const avatarCountVariants = cva('h-full w-full overflow-hidden rounded-full', {
  variants: {
    group: {
      default: '',
      group: 'ring-2 ring-white dark:ring-gray-900'
    }
  },
  defaultVariants: {
    group: 'default'
  }
})

const avatarCountFallbackVariants = cva(
  'font-medium text-gray-700 dark:text-gray-300',
  {
    variants: {
      size: {
        '2xs': 'text-6xs',
        xs: 'text-6xs',
        sm: 'text-5xs',
        md: 'text-2xs',
        lg: 'text-sm',
        xl: 'text-base',
        '2xl': 'text-lg',
        '3xl': 'text-xl'
      }
    },
    defaultVariants: {
      size: '3xl'
    }
  }
)

type ImageLoadingStatus = 'idle' | 'loading' | 'loaded' | 'error'
function useImageLoadingStatus(src?: string) {
  const [loadingStatus, setLoadingStatus] = useState<ImageLoadingStatus>('idle')

  useEffect(() => {
    if (['loaded'].includes(loadingStatus)) return

    if (!src) {
      setLoadingStatus('error')
      return
    }

    let isMounted = true
    const image = new window.Image()

    const updateStatus = (status: ImageLoadingStatus) => () => {
      if (!isMounted) return
      setLoadingStatus(status)
    }

    setLoadingStatus('loading')
    image.onload = updateStatus('loaded')
    image.onerror = updateStatus('error')
    image.src = src

    return () => {
      isMounted = false
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src])

  return loadingStatus
}

export const getShortName = (alt: string) => {
  if (alt) {
    const words = alt?.trim().split(' ')
    if (alt.length == 0) {
      return 'N/A'
    } else if (words?.length == 1) {
      return words[0] ? String(words[0][0])?.toUpperCase() : ''
    } else {
      if (words[0] && words[0][0]) {
        return String(
          words[0][0] + ((words[words?.length - 1] || [])[0] || '')
        ).toUpperCase()
      }
    }
  }
  return 'N/A'
}

type AvatarSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl'

interface AvatarProps {
  ref?: React.Ref<HTMLDivElement>
  size?: AvatarSize
  src?: string
  alt?: string
  count?: number
  statusIcon?:
    | 'online'
    | 'verified'
    | 'offline'
    | 'default'
    | 'cancelAttendInterview'
    | 'attendInterview'
  className?: string
  classNameWrapper?: string
  classNameAvatar?: string
  classNameAvatarFallback?: string
  isDisabled?: boolean
  color?: string
  excludeRandColor?: string[]
  isGroup?: boolean
  tabIndex?: number
  shape?: 'rounded' | 'circular'
  onClick?: MouseEventHandler<HTMLDivElement>
  defaultAvatar?: boolean
  typeUpload?: boolean

  colorFillLogo?: 'primary' | 'white'
  isLogo?: boolean
}

const Avatar = ({
  ref,
  size = '3xl',
  isDisabled = false,
  color = '',
  statusIcon = 'default',
  shape = 'circular',
  className = '',
  classNameWrapper = '',
  classNameAvatar = '',
  classNameAvatarFallback = '',
  onClick,
  defaultAvatar = true,
  colorFillLogo = 'primary',
  excludeRandColor = [],
  isLogo = false,
  typeUpload = false,
  ...props
}: AvatarProps) => {
  const imageLoadingStatus = useImageLoadingStatus(props.src)
  const imageFocusRef = useRef<HTMLImageElement>(null)
  const imageDefaultFocusRef = useRef<HTMLImageElement>(null)
  const gerRandomColor = useMemo(() => {
    const flatColors = Object.keys(listAvatarInitials)
      .filter((colorCode) => !excludeRandColor.includes(colorCode))
      .map((key) => listAvatarInitials[key])
    const randomIndex = randomColor(0, flatColors.length - 1)
    return listAvatarInitials[color?.toUpperCase()] || flatColors[randomIndex]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color, props.alt, excludeRandColor])

  if (props.count) {
    return (
      <div className={cn(avatarCountRootVariants({ size }))}>
        <div
          className={cn(
            avatarCountVariants({
              group: props.isGroup ? 'group' : 'default',
              className
            })
          )}>
          <span className="flex h-full w-full items-center justify-center bg-gray-200 font-medium dark:bg-gray-600">
            <span className={cn(avatarCountFallbackVariants({ size }))}>
              +{props.count > 99 ? '99' : props.count}
            </span>
          </span>
        </div>
      </div>
    )
  }

  const renderStatus = () => {
    if (statusIcon === 'offline') {
      return (
        <span
          className={cn(
            avatarStatusVariants({ size, shape, status: 'offline' })
          )}
        />
      )
    }

    if (statusIcon === 'online') {
      return (
        <span
          className={cn(
            avatarStatusVariants({ size, shape, status: 'online' })
          )}
        />
      )
    }

    if (statusIcon === 'verified') {
      return (
        <span className={cn(avatarVerifyVariants({ size, shape }))}>
          <VerifyIcon />
        </span>
      )
    }

    if (statusIcon === 'attendInterview') {
      return (
        <span className={cn(avatarVerifyVariants({ size, shape }))}>
          <AttendIcon />
        </span>
      )
    }

    if (statusIcon === 'cancelAttendInterview') {
      return (
        <span className={cn(avatarVerifyVariants({ size, shape }))}>
          <CancelAttendIcon />
        </span>
      )
    }

    return null
  }

  const renderAvatarPlaceholderIcon = () => {
    return (
      <span
        ref={imageDefaultFocusRef}
        className={cn(avatarPlaceholderVariants({ shape }))}>
        {defaultAvatar ? <DefaultAvatarImg /> : <DefaultLogoImg />}
      </span>
    )
  }

  if (isLogo) return <LogoATS colorFillLogo={colorFillLogo} />

  return (
    <div
      className={cn(
        'relative flex items-center justify-center',
        classNameWrapper
      )}
      onClick={onClick}>
      <span
        ref={ref}
        className={cn(
          avatarRootVariants({
            size,
            shape,
            group: props.isGroup ? 'group' : 'default',
            className
          })
        )}>
        {props.src && imageLoadingStatus !== 'error' ? (
          <img
            tabIndex={props.tabIndex}
            ref={imageFocusRef}
            className={cn(
              avatarVariants({
                shape,
                group: props.isGroup ? 'group' : 'default',
                className: classNameAvatar
              })
            )}
            src={props.src}
            alt={props.alt}
          />
        ) : (
          <div
            className={cn(
              avatarFallbackRootVariants({
                shape,
                group: props.isGroup ? 'group' : 'default',
                className
              })
            )}>
            {props?.alt && defaultAvatar ? (
              <span
                tabIndex={props.tabIndex}
                ref={imageDefaultFocusRef}
                className={cn(
                  avatarFallbackVariants({
                    size,
                    className: classNameAvatarFallback
                  }),
                  avatarFallbackFocusVariants({
                    shape,
                    className: gerRandomColor
                  })
                )}>
                {getShortName(props.alt)}
              </span>
            ) : (
              renderAvatarPlaceholderIcon()
            )}
          </div>
        )}

        {typeUpload ? (
          <div
            onClick={() => {
              if (!isDisabled) {
                imageFocusRef?.current?.focus()
                imageDefaultFocusRef?.current?.focus()
              }
            }}
            className={cn(
              avatarUploadOverlayVariants({
                size,
                disabled: isDisabled ? 'disabled' : 'default',
                shape
              })
            )}>
            <div
              className={cn(
                avatarUploadOverlayIconVariants({
                  size
                })
              )}>
              <CameraIcon />
            </div>
          </div>
        ) : (
          <div
            onClick={() => {
              if (!isDisabled) {
                imageFocusRef?.current?.focus()
                imageDefaultFocusRef?.current?.focus()
              }
            }}
            className={cn(
              avatarOverlayVariants({
                size,
                disabled: isDisabled ? 'disabled' : 'default',
                alt: props.alt ? 'alt' : 'default',
                shape
              })
            )}
          />
        )}
      </span>

      {renderStatus()}
    </div>
  )
}

Avatar.displayName = 'Avatar'

export { Avatar, listAvatarInitials }
export type { AvatarProps, AvatarSize }
